/**
 * Shared Cell Renderers for DataTable
 */

import { format, isValid, parse } from 'date-fns';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatDaysDuration, getDaysSince } from '@/utils/dateUtils';
import { formatRelativeTime, parseTaskDate } from '@/utils/taskDateUtils';

// Empty Cell

export const EmptyCell = () => <span className="text-gray-400">—</span>;

// Date Renderers

interface DateFormatOptions {
  format?: 'short' | 'medium' | 'long';
}

export function renderAbsoluteDate(
  value: unknown,
  options: DateFormatOptions = {},
): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <EmptyCell />;
  }

  if (typeof value !== 'string') {
    return <EmptyCell />;
  }

  // Try parsing as ISO date format (YYYY-MM-DD) first to avoid timezone issues.
  // Using date-fns parse() treats the string as a local date, not UTC.
  // This prevents the "off by one day" bug when the browser is west of UTC.
  let date: Date;
  const isoDateParsed = parse(value, 'yyyy-MM-dd', new Date());
  if (isValid(isoDateParsed)) {
    date = isoDateParsed;
  } else {
    // Fallback to native Date parsing for datetime strings
    date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return <EmptyCell />;
    }
  }

  const { format: formatOption = 'short' } = options;

  const dateFormatString =
    formatOption === 'long'
      ? 'MMM d, yyyy'
      : formatOption === 'medium'
        ? 'MMM d, yy'
        : 'MM/dd/yyyy';

  return (
    <span className="text-black dark:text-white">
      {format(date, dateFormatString)}
    </span>
  );
}

// Relative Date Renderers

interface RelativeDateOptions {
  staleDaysThreshold?: number;
  showStaleHighlight?: boolean;
}

export function isDateStale(
  dateString: string,
  staleDaysThreshold: number,
): boolean {
  const date = parseTaskDate(dateString);
  if (!date) return false;

  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  return diffInDays > staleDaysThreshold;
}

export function renderRelativeDate(
  value: unknown,
  options: RelativeDateOptions = {},
): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <EmptyCell />;
  }

  if (typeof value !== 'string') {
    return <EmptyCell />;
  }

  const relativeTime = formatRelativeTime(value);
  if (!relativeTime) {
    return <EmptyCell />;
  }

  const date = parseTaskDate(value);
  const formattedDate = date
    ? date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

  const { staleDaysThreshold, showStaleHighlight = true } = options;
  const isStale =
    showStaleHighlight &&
    staleDaysThreshold !== undefined &&
    isDateStale(value, staleDaysThreshold);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`cursor-help ${
            isStale
              ? 'text-red-600 dark:text-red-500'
              : 'text-black dark:text-white'
          }`}
        >
          {relativeTime}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-xs">{formattedDate}</p>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Renders a duration since a date (e.g., "5 days", "< 1 day").
 * Uses shared duration utilities for consistency with badges.
 */
export function renderDuration(value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <EmptyCell />;
  }

  if (typeof value !== 'string') {
    return <EmptyCell />;
  }

  const days = getDaysSince(value);
  const displayText = formatDaysDuration(days);

  if (!displayText) {
    return <EmptyCell />;
  }

  const date = parseTaskDate(value);
  const formattedDate = date
    ? date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help text-black dark:text-white">
          {displayText}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-xs">{formattedDate}</p>
      </TooltipContent>
    </Tooltip>
  );
}

// Text Renderers

export function renderTruncatedText(value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <EmptyCell />;
  }

  const text = String(value);

  return (
    <span className="block truncate text-black dark:text-white" title={text}>
      {text}
    </span>
  );
}

export function renderSecondaryText(value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <EmptyCell />;
  }

  const text = String(value);

  return (
    <span
      className="block truncate text-gray-700 dark:text-zinc-300"
      title={text}
    >
      {text}
    </span>
  );
}

// Person Name Renderer

interface PersonRef {
  first_name: string;
  last_name: string;
}

export function formatPersonName(person: PersonRef | null): string | null {
  if (!person) return null;
  return `${person.first_name} ${person.last_name}`;
}

export function renderPersonName(person: PersonRef | null): React.ReactNode {
  const name = formatPersonName(person);
  if (!name) return <EmptyCell />;
  return <span className="text-black dark:text-white">{name}</span>;
}
